import { reportHandledException } from '@/lib/error-reporting'
import { inviteMember } from './api-client'

const PENDING_INVITE_PREFIX = 'kiao:pendingTeamInvite:v2'
const LEGACY_PENDING_INVITE_KEY = 'kiao:pendingTeamInvite'
const PENDING_INVITE_TTL_MS = 30 * 60_000

export interface PendingTeamInvite {
  email: string
  tradeId: number | null
  attemptId: string
  expiresAt: number
}

export function makeInviteAttemptId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return `invite-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function pendingInviteKey(organizationId: number, ownerEmail: string): string {
  return `${PENDING_INVITE_PREFIX}:${organizationId}:${encodeURIComponent(ownerEmail.trim().toLowerCase())}`
}

export function persistPendingInvite(input: {
  email: string
  organizationId: number
  ownerEmail: string | null
  attemptId: string
  tradeId?: number
}): void {
  if (typeof window === 'undefined' || !input.ownerEmail) return
  try {
    const pending: PendingTeamInvite = {
      email: input.email,
      tradeId: input.tradeId ?? null,
      attemptId: input.attemptId,
      expiresAt: Date.now() + PENDING_INVITE_TTL_MS,
    }
    window.localStorage.setItem(
      pendingInviteKey(input.organizationId, input.ownerEmail),
      JSON.stringify(pending)
    )
  } catch (cause) {
    reportHandledException(cause, { source: 'features/team/pending-team-invite.ts' })
    console.warn('team: could not persist pending invite', cause)
  }
}

export function readPendingInvite(
  organizationId: number,
  ownerEmail: string | null
): PendingTeamInvite | null {
  if (typeof window === 'undefined' || !ownerEmail) return null
  try {
    window.localStorage.removeItem(LEGACY_PENDING_INVITE_KEY)
    const key = pendingInviteKey(organizationId, ownerEmail)
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingTeamInvite>
    if (
      typeof parsed.email !== 'string' ||
      !parsed.email.includes('@') ||
      typeof parsed.attemptId !== 'string' ||
      parsed.attemptId.length < 16 ||
      (parsed.tradeId !== null &&
        parsed.tradeId !== undefined &&
        !Number.isInteger(parsed.tradeId)) ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Date.now()
    ) {
      window.localStorage.removeItem(key)
      return null
    }
    return {
      email: parsed.email,
      tradeId: parsed.tradeId ?? null,
      attemptId: parsed.attemptId,
      expiresAt: parsed.expiresAt,
    }
  } catch (cause) {
    reportHandledException(cause, { source: 'features/team/pending-team-invite.ts' })
    console.warn('team: could not read pending invite', cause)
    return null
  }
}

export function clearPendingInvite(organizationId: number, ownerEmail: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(pendingInviteKey(organizationId, ownerEmail))
  } catch (cause) {
    reportHandledException(cause, { source: 'features/team/pending-team-invite.ts' })
    console.warn('team: could not clear pending invite', cause)
  }
}

export async function resumePendingTeamInviteAfterPayment(
  organizationId: number,
  ownerEmail: string,
  tradeId: number
): Promise<void> {
  const pending = readPendingInvite(organizationId, ownerEmail)
  if (!pending || pending.tradeId !== tradeId) return
  await inviteMember(organizationId, pending.email, pending.attemptId)
  clearPendingInvite(organizationId, ownerEmail)
}
